import { useTranslation } from "react-i18next";
import { MobileCardRow, MobileCardWrapper, TableBody, TableColumn, TableHeader, TableRow, TableCell } from "./dashboard/table-components";
import { SearchIcon, ArrowLeftIcon, ArrowRightIcon, DownloadIcon } from "../assets/dashboard-icon.svg";
import { useRef } from "react";
import { formatAmount, formatDate, getStatusBadgeClass, getStatusForSalaryStatus, mapInvoiceStatus } from "../helper/common.helper";
import { useSalaries } from "../hooks/useSalaries";
import { SalaryRecord } from "../api/salary";
import { downloadSalarySlipAsPdf } from "../../common/invoiceDownloadHelper";
import LoadingCard from '../components/SkeletonLoading'

export const SalaryNoticeSection = ({
    className = '',
}: {
    className?: string
}
) => {
    const { t } = useTranslation();
    const dropdownRef = useRef<HTMLDivElement>(null);
    const isMobile = window.innerWidth <= 768;
    const PAGE_SIZE = 10
    const {
        loading,
        error,
        currentPage,
        totalPages,
        totalCount,
        searchQuery,
        salaries,
        setCurrentPage,
        setSearchQuery,
        goToNextPage,
        goToPreviousPage,
    } = useSalaries(PAGE_SIZE);
    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchQuery(e.target.value);
    };

    const getPageNumbers = () => {
        const pages: (number | string)[] = [];
        const maxPagesToShow = 5;

        if (totalPages <= maxPagesToShow) {
            for (let i = 1; i <= totalPages; i++) {
                pages.push(i);
            }
        } else {
            pages.push(1);
            if (currentPage > 3) pages.push('...');
            for (
                let i = Math.max(2, currentPage - 1);
                i <= Math.min(totalPages - 1, currentPage + 1);
                i++
            ) {
                if (!pages.includes(i)) pages.push(i);
            }
            if (currentPage < totalPages - 2) pages.push('...');
            pages.push(totalPages);
        }
        return pages;
    };


        return (
        <div className={`invoice-table-container ${className}`}>
            <div className="invoice-table-header">
                <h3 className="invoice-table-title">{t('MY_SALARIES')}</h3>

                <div className="invoice-table__filter-bar">
                    <div className="invoice-table__search-input" ref={dropdownRef}>
                        <SearchIcon />
                        <input
                            type="text"
                            placeholder={t('HEADER_SEARCH') || 'Search'}
                            value={searchQuery}
                            onChange={handleSearchChange}
                            className="invoice-table__search-field"
                        />
                    </div>
                </div>
            </div>


            <div className="invoice-table-content">
                

                {/* Table */}
                <div className="salary-table-scroll-wrapper">
                    <div className="invoice-table">
                        
                        {!isMobile && (
                            <TableHeader>
                                <TableColumn>{t('SERIAL_ID')}</TableColumn>
                                <TableColumn>{t('CLIENT_NAME')}</TableColumn>
                                <TableColumn>{t('AMOUNT')}</TableColumn>
                                <TableColumn>{t('SALARY_TYPE')}</TableColumn>
                                <TableColumn>{t('INVOICE_NO')}</TableColumn>
                                <TableColumn>{t('CREATED_DATE')}</TableColumn>
                                <TableColumn>{t('INVOICE_STATUS')}</TableColumn>
                                <TableColumn>{t('SALARY_STATUS')}</TableColumn>
                                <TableColumn>{t('ACTION_LABEL')}</TableColumn>
                            </TableHeader>
                        )}
                        
                        
                        <TableBody>
           
                            {salaries.length === 0 && (
                                <div className="invoice-table">
                                    {loading && (
                                <div >
                                <LoadingCard/></div>
                            )}
                                    <div className="invoice-table-content">
                                        {/* No drafts found message */}
                                        {(salaries.length === 0) && (
                                            <div className='d-flex justify-content-center flex-column align-items-center pt-5'>
                                                <span className='pt-3'>{t('NO_SALARIES_FOUND')}.</span>
                                                {error}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                            {isMobile ? (
                                // Mobile: Card-based layout
                                salaries?.map((salary: SalaryRecord, index: number) => (
                                    <MobileCardWrapper key={salary.id || salary.id}>
                                        <MobileCardRow label={t('ID')}>
                                            <span className="invoice-table-detailed__invoice-link">{(currentPage - 1) * PAGE_SIZE + index + 1}</span>
                                        </MobileCardRow>
                                        <MobileCardRow label={t('CLIENT_NAME')}>
                                            {salary.clientName || 'N/A'}
                                        </MobileCardRow>
                                        <MobileCardRow label={t('AMOUNT')}>
                                            {formatAmount(Number(salary.amount)) || 'N/A'} {t('kr')}
                                        </MobileCardRow>
                                        <MobileCardRow label={t('SALARY_TYPE')}>
                                            {salary.salaryType === 1
                                                ? t("INVOICE")
                                                : salary.salaryType === 2   
                                                    ? t("HOLIDAY_PAYOUT")
                                                    : t("MANUAL_SALARY")}
                                        </MobileCardRow>
                                        <MobileCardRow label={t('INVOICE_NO')}>
                                            {salary?.invoiceNumber || 'N/A'}
                                        </MobileCardRow>
                                        <MobileCardRow label={t('CREATED_DATE')}>
                                            {formatDate(salary.createdDate) || 'N/A'}
                                        </MobileCardRow>
                                        <MobileCardRow label={t('INVOICE_STATUS')}>
                                            <span className={getStatusBadgeClass(salary?.invoiceStatus || 0)}>
                                                {t(mapInvoiceStatus(salary?.invoiceStatus)) || '-'}
                                            </span>
                                        </MobileCardRow>
                                        <MobileCardRow label={t('SALARY_STATUS')}>
                                            <span className={getStatusBadgeClass(salary?.salaryStatus || 0)}>
                                                {salary.salaryStatus === 1 ? t('PAID') : t('UNPAID') || 'N/A'}
                                            </span>
                                        </MobileCardRow>

                                        <MobileCardRow label={t('ACTION_LABEL')}>
                                            <div className="invoice-table-detailed__action-group">
                                                <button
                                                    className="invoice-table-detailed__action-btn"
                                                    title={t("DOWNLOAD")}
                                                    onClick={() => {
                                                        const identifier =
                                                            salary?.salaryType === 3
                                                                ? salary?.id || salary?.id
                                                                : salary?.invoiceNumber;

                                                        downloadSalarySlipAsPdf(identifier, 'sv-se', salary.salaryType,);
                                                    }}
                                                >
                                                    <DownloadIcon />
                                                </button>
                                            </div>
                                        </MobileCardRow>
                                    </MobileCardWrapper>
                                ))
                            ) : (
                                salaries.map((salary: SalaryRecord, index: number) => (
                                    <TableRow key={salary.id}>
                                        <TableCell>
                                            <span className="invoice-table-detailed__invoice-link">{(currentPage - 1) * PAGE_SIZE + index + 1}</span>
                                        </TableCell>
                                        <TableCell>{salary?.clientName || 'N/A'}</TableCell>
                                        <TableCell>{formatAmount(Number(salary.amount)) || 'N/A'}{t('kr')}</TableCell>
                                        <TableCell>{
                                            salary.salaryType === 1
                                                ? t("INVOICE")
                                                : salary.salaryType === 2
                                                    ? t("HOLIDAY_PAYOUT")
                                                    : t("MANUAL_SALARY")

                                                    || '-'}</TableCell>
                                        <TableCell>{salary?.invoiceNumber || '-'}</TableCell>
                                        <TableCell>{formatDate(salary.createdDate) || '-'}</TableCell>
                                        <TableCell>
                                            <span className={getStatusBadgeClass(salary?.invoiceStatus || 0)}>
                                                {t(mapInvoiceStatus(salary?.invoiceStatus)) || '-'}
                                            </span>
                                        </TableCell>
                                        <TableCell>
                                            <span className={getStatusForSalaryStatus(salary.salaryStatus)}>
                                                {salary.salaryStatus === 1 ? t('PAID') : t('UNPAID')}
                                            </span>
                                        </TableCell>
                                        <TableCell>
                                            <div className="invoice-table-detailed__action-group">
                                                <button
                                                    className="invoice-table-detailed__action-btn"
                                                    title={t("DOWNLOAD")}
                                                    onClick={() => {
                                                        const identifier =
                                                            salary?.salaryType === 3
                                                                ? salary?.id || salary?.id
                                                                : salary?.invoiceNumber;

                                                        downloadSalarySlipAsPdf(identifier, 'sv-se', salary.salaryType,);
                                                    }}
                                                >
                                                    <DownloadIcon />
                                                </button>
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </div>
                </div>


                {/* Pagination */}
                {totalPages > 1 && (
                    <div className="invoice-table-pagination">
                        <div className="pagination-controls">
                            <button
                                className="pagination-button pagination-button--prev"
                                onClick={goToPreviousPage}
                                disabled={currentPage === 1}
                            >
                                <ArrowLeftIcon />
                                <span>{t('PREV_STEP') || 'Prev'}</span>
                            </button>

                            {getPageNumbers().map((page, index) => {
                                if (page === '...') {
                                    return (
                                        <button
                                            key={`ellipsis-${index}`}
                                            className="pagination-button pagination-button--number"
                                            disabled
                                        >
                                            ...
                                        </button>
                                    );
                                }
                                return (
                                    <button
                                        key={page}
                                        className={`pagination-button pagination-button--number${page === currentPage ? ' pagination-button--active' : ''
                                            }`}
                                        onClick={() => setCurrentPage(page as number)}
                                    >
                                        {page}
                                    </button>
                                );
                            })}

                            <button
                                className="pagination-button pagination-button--next"
                                onClick={goToNextPage}
                                disabled={currentPage === totalPages}
                            >
                                <span>{t('NEXT_STEP') || 'Next'}</span>
                                <ArrowRightIcon />
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}